import { useMemo } from "react";
import { VoyageProgressBarVoyageFragment } from "__generated__/view/voyages/graphql/voyage.fragments.generated";
import { CompactLocationFragment } from "__generated__/view/locations/graphql/location.fragments.generated";

type VoyagePoint = {
	callId: number | null;
	location: Pick<CompactLocationFragment, "id" | "description"> | null | undefined;
	time: { atd: number | null | undefined, ata: number | null | undefined, pta: number | null | undefined };
	isSimulated: boolean;
	statusColor: string | undefined;
	isFinished: boolean;
	isBoundary: boolean;
};

type UseVoyageProgressBarReturn = {
	points: VoyagePoint[];
	activeCall: VoyageProgressBarVoyageFragment["calls"][number] | undefined;
	activePointIndex: number;
	percentage: number;
	isEnRoute: boolean;
	allFinished: boolean;
	startPoint: VoyagePoint;
	endPoint: VoyagePoint;
};

const useVoyageProgressBar = (voyage: VoyageProgressBarVoyageFragment): UseVoyageProgressBarReturn => {
	return useMemo(() => {
		const orderedCalls = voyage.calls.sort((a, b) => (a.pta ?? 0) - (b.pta ?? 0)).filter(c => !!c);

		const firstCall = orderedCalls[0];
		const startSameAsFirstCall = voyage.from?.id === firstCall?.location?.id;

		const lastCall = orderedCalls.at(-1);
		const endSameAsLastCall = voyage.to?.id === lastCall?.location?.id;

		const allPoints: VoyagePoint[] = [];

		// start point (first call / voyage start)
		allPoints.push({
			callId: startSameAsFirstCall ? firstCall?.id : null,
			location: voyage.from || firstCall?.location,
			time: startSameAsFirstCall
				? { atd: firstCall?.atd, ata: firstCall?.ata, pta: firstCall?.pta }
				: { atd: undefined, ata: undefined, pta: voyage.start },
			statusColor: startSameAsFirstCall ? firstCall?.lastStatusChange?.status.color : undefined,
			isFinished: startSameAsFirstCall ?  !!firstCall?.atd : true,
			isSimulated: startSameAsFirstCall ? !firstCall?.eta && !firstCall?.ata && !!firstCall?.simulatedTimestamp  : false,
			isBoundary: true,
		});

		// intermediate calls
		orderedCalls.forEach((call, index) => {
			const isFirstAndCombined = index === 0 && startSameAsFirstCall;
			const isLastAndCombined = index === orderedCalls.length - 1 && endSameAsLastCall;

			if (!isFirstAndCombined && !isLastAndCombined) {
				allPoints.push({
					callId: call.id,
					location: call.location,
					time: { atd: call.atd, ata: call.ata, pta: call.pta },
					statusColor: call.lastStatusChange?.status.color,
					isFinished: !!call.atd,
					isSimulated: !call.eta && !call.ata && call.simulatedTimestamp,
					isBoundary: false,
				});
			}
		});

		// end point (last call / voyage end)
		if (lastCall) {
			allPoints.push({
				callId: endSameAsLastCall ? lastCall.id : null,
				location: voyage.to || lastCall.location,
				time: endSameAsLastCall
					? { atd: lastCall.atd, ata: lastCall.ata, pta: lastCall.pta }
					: { atd: undefined, ata: undefined, pta: voyage.end },
				statusColor: endSameAsLastCall ? lastCall.lastStatusChange?.status.color : undefined,
				isFinished: endSameAsLastCall ?  !!lastCall.atd : allPoints.slice(1, -1).every(p => p.isFinished),
				isSimulated: endSameAsLastCall ? !lastCall.etd && !lastCall.atd && !!lastCall?.simulatedTimestamp : false,
				isBoundary: true,
			});
		}

		// find out which call is currently active based on ATA and ATD
		const lastFinishedCallIndex = orderedCalls.findLastIndex(call => !!call.atd);
		const lastArrivedCallIndex = orderedCalls.findLastIndex(call => !!call.ata);
		const allFinished = lastFinishedCallIndex === orderedCalls.length - 1;

		const activeCallIndex = Math.max(
			lastArrivedCallIndex,
			lastFinishedCallIndex === -1 ? 0 : Math.min(lastFinishedCallIndex + 1, orderedCalls.length - 1),
		);
		const activeCall = orderedCalls[activeCallIndex];
		const activePointIndex = allPoints.findIndex(point => point.callId === activeCall?.id);

		const hasAta = !!activeCall?.ata;
		const isEnRoute = !hasAta && activePointIndex > 0 && activeCallIndex > lastFinishedCallIndex;

		let percentage = 0;
		if (activePointIndex !== -1) {
			if (isEnRoute) {
				percentage = ((activePointIndex - 0.5) / (allPoints.length - 1)) * 100;
			} else {
				percentage = (activePointIndex / (allPoints.length - 1)) * 100;
			}
		}

		return {
			points: allPoints,
			activeCall,
			activePointIndex,
			percentage,
			isEnRoute,
			allFinished,
			startPoint: allPoints[0],
			endPoint: allPoints[allPoints.length - 1],
		};
	}, [ voyage ]);
};

export default useVoyageProgressBar;
